import { useEffect, useState } from 'react';
import { Get } from '../helpers/AxiosHelper';
import {
  Block,
  BlockHeader,
  BlockTitle,
  Button,
  Card,
  Chip,
  Link,
  List,
  ListItem,
  Navbar,
  Page,
  Panel,
  Range,
  Segmented,
  SegmentedButton,
} from 'konsta/react';
import TabBar from '../components/TabBar';
import { useAppContext } from '../context/AppContext';
import { formatUSD } from '../utils';
import { FaArrowLeftLong, FaCartPlus, FaFilter } from 'react-icons/fa6';
import { Product } from '../models/Models';
import { useNavigate } from 'react-router-dom';
import { mixTrack } from '../helpers/MixPanelHelper';

const defaultConfig = {
  changed: false,
  reset: false,
  price: {
    min: 0,
    max: 0,
  },
  avgProfitPerDay: {
    min: 0,
    max: 0,
  },
};

const Store = () => {
  const [leftPanelOpened, setLeftPanelOpened] = useState(false);
  const [filterConfigs, setFilterConfigs] = useState({
    price: {
      min: 0,
      max: 0,
    },
    avgProfitPerDay: {
      min: 0,
      max: 0,
    },
  });

  const [filters, setFilters] = useState(defaultConfig);
  const { products } = useAppContext();
  const [productList, setProductList] = useState<Product[]>([]);
  const [tab, setTab] = useState<'new' | 'used'>('new');
  const navigate = useNavigate();

  useEffect(() => {
    if (products && productList.length === 0) {
      const { minPrice, maxPrice } = products.reduce(
        (acc, item) => ({
          maxPrice: Math.max(acc.maxPrice, item.price),
          minPrice: Math.min(acc.minPrice, item.price),
        }),
        { minPrice: products[0].price, maxPrice: 1000 }, // Initial values
      );

      const { minProfitPerDay, maxProfitPerDay } = products.reduce(
        (acc, item) => ({
          maxProfitPerDay: Math.max(acc.maxProfitPerDay, item.avgProfitDay),
          minProfitPerDay: Math.min(acc.minProfitPerDay, item.avgProfitDay),
        }),
        { minProfitPerDay: products[0].avgProfitDay, maxProfitPerDay: 0.01 }, // Initial values
      );

      setFilterConfigs((prevState) => ({
        ...prevState, // Keep existing properties
        price: {
          min: minPrice, // Update min value
          max: maxPrice, // Update max value
        },
        avgProfitPerDay: {
          min: minProfitPerDay, // Update min value
          max: maxProfitPerDay, // Update max value
        },
      }));

      setFilters((prevState) => ({
        ...prevState,
        reset: true,
        price: {
          min: minPrice,
          max: maxPrice,
        },
        avgProfitPerDay: {
          min: minProfitPerDay,
          max: maxProfitPerDay,
        },
      }));

      setProductList(products.filter((c) => !c.isUsed));
    }
  }, [products]);

  useEffect(() => {
    if (filters.changed) {
      setProductList(
        products?.filter(
          (c) =>
            (tab === 'new' ? !c.isUsed : c.isUsed) &&
            c.price >= filters.price.min &&
            c.price <= filters.price.max &&
            c.avgProfitDay >= filters.avgProfitPerDay.min &&
            c.avgProfitDay <= filters.avgProfitPerDay.max,
        ) ?? [],
      );
    }
  }, [filters, tab]);

  function resetFilters() {
    setProductList(
      products?.filter((c) => (tab === 'new' ? !c.isUsed : c.isUsed)) ?? [],
    );
    setFilters((prevState) => ({
      ...prevState,
      price: {
        min: filterConfigs.price.min,
        max: filterConfigs.price.max,
      },
      avgProfitPerDay: {
        min: filterConfigs.avgProfitPerDay.min,
        max: filterConfigs.avgProfitPerDay.max,
      },
      reset: true,
    }));
  }

  return (
    <Page className="select-none pb-40">
      <Navbar
        title="Marketplace"
        className="top-0 sticky translucent"
        left={
          <FaArrowLeftLong
            className="mx-3 w-5 h-5"
            onClick={() => navigate(-1)}
          />
        }
        right={
          <span
            className="text-primary font-bold flex items-center cursor-pointer"
            onClick={() => setLeftPanelOpened(true)}
          >
            Filters <FaFilter className="ms-1" />
          </span>
        }
        subnavbar={
          products && products.length > 0 ? (
            <Segmented strong>
              <SegmentedButton
                strong
                active={tab === 'new'}
                onClick={() => {
                  setFilters((prev) => ({
                    ...prev,
                    changed: true,
                  }));
                  setTab('new');
                }}
              >
                New
              </SegmentedButton>
              <SegmentedButton
                strong
                active={tab === 'used'}
                onClick={() => {
                  setFilters((prev) => ({
                    ...prev,
                    changed: true,
                  }));
                  setTab('used');
                }}
              >
                Pre-Owned
              </SegmentedButton>
            </Segmented>
          ) : undefined
        }
      />

      {products && (
        <>
          {productList &&
            productList.length > 0 &&
            productList.map((p) => (
              <Card
                key={!p.isUsed ? p.id : `${p.id}_${p.userProductId}`}
                outline
                colors={{
                  outlineMaterial:
                    'border-md-light-primary dark:border-md-dark-primary',
                }}
                header={
                  <h1 className="currency tracking-widest">
                    {p.name}{' '}
                    {p.isUsed && (
                      <Chip
                        colors={{
                          fillBgMaterial: 'bg-yellow-500',
                          fillTextMaterial: 'text-black',
                        }}
                      >
                        Used
                      </Chip>
                    )}
                  </h1>
                }
                footer={
                  <div className="flex justify-between items-center">
                    <h1 className="text-primary text-xl currency">
                      {formatUSD(p.price)}
                    </h1>
                    <div>
                      {p.isAvailable && (
                        <Button
                          small
                          onClick={() => {
                            mixTrack('[View] Product', {
                              productId: p.id,
                              name: p.name,
                              price: p.price,
                              isUsed: p.isUsed,
                            });

                            navigate(
                              `/product/${p.id}${p.isUsed ? '/' + p.userProductId : ''}`,
                            );
                          }}
                        >
                          Purchase <FaCartPlus className="ms-1" />
                        </Button>
                      )}
                      {!p.isAvailable && (
                        <span className="text-red-500">Out of stock</span>
                      )}
                    </div>
                  </div>
                }
              >
                <div className="flex justify-between mb-2">
                  <div className="flex flex-col space-y-1 items-center bg-md-dark-surface rounded-lg p-2">
                    <span className="currency tracking-widest">
                      {formatUSD(p.avgProfitDay)}
                    </span>
                    <span>Income/Day</span>
                  </div>
                  <div className="flex flex-col space-y-1 items-center bg-md-dark-surface rounded-lg p-2">
                    <span className="currency tracking-widest">
                      {formatUSD(p.isUsed ? 0 : p.setupFee)}
                    </span>
                    <span>Setup Fee</span>
                  </div>
                  {!p.isUsed && (
                    <div className="flex flex-col space-y-1 items-center bg-md-dark-surface rounded-lg p-2">
                      <span className="currency tracking-widest">
                        {p.setupTimeHours}
                      </span>
                      <span>Hours to setup</span>
                    </div>
                  )}
                  {p.isUsed && (
                    <div className="flex flex-col space-y-1 items-center bg-md-dark-surface rounded-lg p-2">
                      <span>Immediate</span>
                      <span>Transfer</span>
                    </div>
                  )}
                </div>
                {p.description}
              </Card>
            ))}

          {(!productList || !productList.length) && (
            <Block className="text-center text-lg">No products found</Block>
          )}
        </>
      )}

      <br />
      <br />
      <br />
      <br />

      <Panel
        side="left"
        opened={leftPanelOpened}
        onBackdropClick={() => setLeftPanelOpened(false)}
      >
        <Page>
          <Navbar
            title="Filters"
            right={
              <Link navbar onClick={() => resetFilters()}>
                Reset
              </Link>
            }
          />
          <Block className="space-y-4">
            <BlockTitle className="mb-1">
              Min. Price ({formatUSD(filters.price.min)})
            </BlockTitle>
            <List strong insetMaterial>
              <ListItem
                innerClassName="flex flex-col rtl:space-x-reverse"
                innerChildren={
                  <>
                    <Range
                      value={filters.price.min}
                      step={1}
                      min={filterConfigs.price.min}
                      max={filterConfigs.price.max}
                      onChange={(e) =>
                        setFilters((prevState) => ({
                          ...prevState,
                          changed: true,
                          reset: false,
                          price: {
                            ...prevState.price,
                            min: Number(e.target.value),
                          },
                        }))
                      }
                    />
                    <div className="flex justify-between items-center">
                      <span>{formatUSD(filterConfigs.price.min)}</span>

                      <span>{formatUSD(filterConfigs.price.max)}</span>
                    </div>
                  </>
                }
              />
            </List>

            <BlockTitle className="mb-1">
              Max. Price ({formatUSD(filters.price.max)})
            </BlockTitle>
            <List strong insetMaterial>
              <ListItem
                innerClassName="flex flex-col rtl:space-x-reverse"
                innerChildren={
                  <>
                    <Range
                      value={filters.price.max}
                      step={1}
                      min={filterConfigs.price.min}
                      max={filterConfigs.price.max}
                      onChange={(e) =>
                        setFilters((prevState) => ({
                          ...prevState,
                          changed: true,
                          reset: false,
                          price: {
                            ...prevState.price,
                            max: Number(e.target.value),
                          },
                        }))
                      }
                    />
                    <div className="flex justify-between items-center">
                      <span>{formatUSD(filterConfigs.price.min)}</span>

                      <span>{formatUSD(filterConfigs.price.max)}</span>
                    </div>
                  </>
                }
              />
            </List>

            <BlockTitle className="mb-1">
              Min. Profit/day ({formatUSD(filters.avgProfitPerDay.min)})
            </BlockTitle>
            <List strong insetMaterial>
              <ListItem
                innerClassName="flex flex-col rtl:space-x-reverse"
                innerChildren={
                  <>
                    <Range
                      value={filters.avgProfitPerDay.min}
                      step={0.01}
                      min={filterConfigs.avgProfitPerDay.min}
                      max={filterConfigs.avgProfitPerDay.max}
                      onChange={(e) =>
                        setFilters((prevState) => ({
                          ...prevState,
                          changed: true,
                          reset: false,
                          avgProfitPerDay: {
                            ...prevState.avgProfitPerDay,
                            min: Number(e.target.value),
                          },
                        }))
                      }
                    />
                    <div className="flex justify-between items-center">
                      <span>
                        {formatUSD(filterConfigs.avgProfitPerDay.min)}
                      </span>

                      <span>
                        {formatUSD(filterConfigs.avgProfitPerDay.max)}
                      </span>
                    </div>
                  </>
                }
              />
            </List>

            <BlockTitle className="mb-1">
              Max. Profit/day ({formatUSD(filters.avgProfitPerDay.max)})
            </BlockTitle>
            <List strong insetMaterial>
              <ListItem
                innerClassName="flex flex-col rtl:space-x-reverse"
                innerChildren={
                  <>
                    <Range
                      value={filters.avgProfitPerDay.max}
                      step={0.01}
                      min={filterConfigs.avgProfitPerDay.min}
                      max={filterConfigs.avgProfitPerDay.max}
                      onChange={(e) =>
                        setFilters((prevState) => ({
                          ...prevState,
                          changed: true,
                          reset: false,
                          avgProfitPerDay: {
                            ...prevState.avgProfitPerDay,
                            max: Number(e.target.value),
                          },
                        }))
                      }
                    />
                    <div className="flex justify-between items-center">
                      <span>
                        {formatUSD(filterConfigs.avgProfitPerDay.min)}
                      </span>

                      <span>
                        {formatUSD(filterConfigs.avgProfitPerDay.max)}
                      </span>
                    </div>
                  </>
                }
              />
            </List>
          </Block>
        </Page>
      </Panel>

      <TabBar selectedTab="store" />
    </Page>
  );
};

export default Store;
